import type { FaceBox as SharedFaceBox } from '@pz/scene';
import { createLayer } from './layers.js';
export { cropForFace } from '@pz/scene';

/**
 * "Which face?" — the question a photo of more than one person forces.
 *
 * A shopper uploading a group photo to a product that carries ONE face has made
 * an ambiguous request, and there are only three ways to resolve it: guess,
 * refuse, or ask. Competitors guess — they take the largest or most central face
 * and the customer discovers the wrong relative on the keychain when it arrives.
 * Asking costs one tap and removes an entire class of remake.
 *
 * The picker deliberately does nothing when there is only one face: a question
 * with one answer is not a question, it is an obstacle.
 */

/** A detected face, in fractions of the image so it survives any resize. */
export type FaceBox = SharedFaceBox;

export interface FacePickOptions {
  /** Where to mount — the widget's shadow root or an overlay host. */
  host: HTMLElement;
  imageUrl: string;
  faces: FaceBox[];
  labels?: { title?: string; hint?: string; cancel?: string; person?: string };
}

const DEFAULT_LABELS = {
  title: 'Koje lice želite?',
  hint: 'Dodirnite osobu koja ide na proizvod.',
  cancel: 'Otkaži',
  /** Screen-reader name for each choice; the number is never shown. */
  person: 'Osoba',
};

/**
 * Asks which face, and resolves with the chosen one.
 *
 * Resolves with `null` if the shopper backs out — the caller should then leave
 * the photo unframed rather than pick for them.
 */
export function pickFace(options: FacePickOptions): Promise<FaceBox | null> {
  const faces = [...options.faces].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
  if (faces.length <= 1) return Promise.resolve(faces[0] ?? null);

  const labels = { ...DEFAULT_LABELS, ...options.labels };

  return new Promise((resolve) => {
    const panel = document.createElement('div');
    panel.className = 'pz-facepick-panel';

    const heading = document.createElement('h3');
    heading.className = 'pz-facepick-title';
    heading.textContent = labels.title;

    const hint = document.createElement('p');
    hint.className = 'pz-facepick-hint';
    hint.textContent = labels.hint;

    const frame = document.createElement('div');
    frame.className = 'pz-facepick-frame';
    const image = document.createElement('img');
    image.className = 'pz-facepick-img';
    image.alt = '';
    image.src = options.imageUrl;
    frame.appendChild(image);

    let settled = false;
    const finish = (face: FaceBox | null): void => {
      if (settled) return;
      settled = true;
      layer.element.classList.add('is-closing');
      layer.close();
      window.setTimeout(() => layer.destroy(), 180);
      resolve(face);
    };

    faces.forEach((face, index) => {
      const spot = document.createElement('button');
      spot.type = 'button';
      spot.className = 'pz-facepick-spot';
      // Percentages, so the boxes track the image at any size — including the
      // phone rotating mid-decision.
      spot.style.left = `${face.x * 100}%`;
      spot.style.top = `${face.y * 100}%`;
      spot.style.width = `${face.w * 100}%`;
      spot.style.height = `${face.h * 100}%`;
      spot.setAttribute('aria-label', `${labels.person} ${index + 1}`);
      spot.innerHTML = `<span class="pz-facepick-num">${index + 1}</span>`;
      spot.addEventListener('click', () => finish(face));
      frame.appendChild(spot);
    });

    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.className = 'pz-facepick-cancel';
    cancel.textContent = labels.cancel;
    cancel.addEventListener('click', () => finish(null));

    panel.append(heading, hint, frame, cancel);

    // The layer carries Escape, the tap-outside, the focus trap and the scroll
    // lock, and puts the sheet in the browser's top layer — a face picker
    // opened from inside the wizard has to sit above it, and a theme wrapper
    // with a transform on it must not be able to clip it.
    const layer = createLayer({
      host: options.host,
      className: 'pz-facepick',
      content: panel,
      label: labels.title,
      // Backing out is an answer of its own: leave the photo unframed rather
      // than choosing a face for the shopper.
      onClose: () => finish(null),
    });
    layer.open();

    // Focus lands on the first face, so a keyboard or screen-reader user is
    // offered the choice rather than having to hunt for it.
    (frame.querySelector('.pz-facepick-spot') as HTMLElement | null)?.focus();
  });
}
